import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LinkedInSidebar from './LinkedInSidebar';
import { Heart, Eye } from "lucide-react";
import axios from "axios";

const FeedPage = ({ currentPage, setCurrentPage, setTitleDescription, openCompanyPage }) => {
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const toggleDescription = (stockId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [stockId]: !prev[stockId]
    }));
  };
  const MAX_LENGTH = 150;

  const [feedCompanies, setFeedCompanies] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const loadMoreRef = useRef(null);
  const pageNumberRef = useRef(1);

  useEffect(() => {
    if (loading) return; // Не запускаем, если уже загружается

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && pageNumberRef.current > 1) {
            getFeeds();
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: '0px',
        threshold: 1.0, // кнопка полностью видна
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [getFeeds]);

  
  // State for managing comments
  const [expandedComments, setExpandedComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [replyComments, setReplyComments] = useState({});
  const [showReplyForm, setShowReplyForm] = useState({});
  
  // Mock comments data
  const [commentsData, setCommentsData] = useState({
    'hero': [
      {
        id: 1,
        author: 'Sarah Chen',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b9d5f1e2?w=150&h=150&fit=crop&crop=face',
        content: 'Great platform! Looking forward to connecting with fellow investors.',
        time: '2h',
        likes: 12,
        replies: [
          {
            id: 11,
            author: 'Mike Johnson',
            avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
            content: 'Absolutely! The analytics here are impressive.',
            time: '1h',
            likes: 3
          }
        ]
      },
      {
        id: 2,
        author: 'David Park',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
        content: 'The real-time data integration is exactly what I needed for my portfolio analysis.',
        time: '3h',
        likes: 8,
        replies: []
      }
    ]
  });

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const handleNewComment = (postId) => {
    const content = newComments[postId];
    if (!content || content.trim() === '') return;

    const newComment = {
      id: Date.now(),
      author: 'You',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      content: content.trim(),
      time: 'now',
      likes: 0,
      replies: []
    };

    setCommentsData(prev => ({
      ...prev,
      [postId]: [newComment, ...(prev[postId] || [])]
    }));

    setNewComments(prev => ({
      ...prev,
      [postId]: ''
    }));
  };

  const handleReply = (postId, commentId) => {
    const content = replyComments[`${postId}-${commentId}`];
    if (!content || content.trim() === '') return;

    const newReply = {
      id: Date.now(),
      author: 'You',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      content: content.trim(),
      time: 'now',
      likes: 0
    };

    setCommentsData(prev => ({
      ...prev,
      [postId]: prev[postId].map(comment => 
        comment.id === commentId 
          ? { ...comment, replies: [newReply, ...comment.replies] }
          : comment
      )
    }));

    setReplyComments(prev => ({
      ...prev,
      [`${postId}-${commentId}`]: ''
    }));

    setShowReplyForm(prev => ({
      ...prev,
      [`${postId}-${commentId}`]: false
    }));
  };

  const toggleReplyForm = (postId, commentId) => {
    const key = `${postId}-${commentId}`;
    setShowReplyForm(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const likeComment = (postId, commentId, isReply = false, parentCommentId = null) => {
    setCommentsData(prev => ({
      ...prev,
      [postId]: prev[postId].map(comment => {
        if (isReply && comment.id === parentCommentId) {
          return {
            ...comment,
            replies: comment.replies.map(reply => 
              reply.id === commentId 
                ? { ...reply, likes: reply.likes + 1 }
                : reply
            )
          };
        } else if (!isReply && comment.id === commentId) {
          return { ...comment, likes: comment.likes + 1 };
        }
        return comment;
      })
    }));
  };

  const CommentSection = ({ postId }) => {
    const comments = commentsData[postId] || [];
    const isExpanded = expandedComments[postId];

    return (
      <>
        {/* Comments Button */}
        <button 
          onClick={() => toggleComments(postId)}
          className="linkedin-action-btn"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Comment ({comments.length})
        </button>

        {/* Expanded Comments Section - Will be rendered separately */}
      </>
    );
  };

  const CommentSectionExpanded = ({ postId }) => {
    const comments = commentsData[postId] || [];
    const isExpanded = expandedComments[postId];

    if (!isExpanded) return null;

    return (
      <div className="border-t border-gray-100 pt-4 px-4 pb-4">
        {/* Add New Comment */}
        <div className="flex space-x-3 mb-4">
          <img 
            src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face" 
            alt="Your avatar" 
            className="w-8 h-8 rounded-full object-cover"
          />
          <div className="flex-1 flex space-x-2">
            <input
              type="text"
              placeholder="Add a comment..."
              value={newComments[postId] || ''}
              onChange={(e) => setNewComments(prev => ({
                ...prev,
                [postId]: e.target.value
              }))}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleNewComment(postId);
                }
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:border-linkedin-blue"
            />
            <button
              onClick={() => handleNewComment(postId)}
              disabled={!newComments[postId] || newComments[postId].trim() === ''}
              className="linkedin-btn-primary text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Post
            </button>
          </div>
        </div>

        {/* Comments List */}
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="space-y-3">
              {/* Main Comment */}
              <div className="flex space-x-3">
                <img 
                  src={comment.avatar} 
                  alt={comment.author} 
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1">
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">{comment.author}</span>
                      <span className="text-xs text-gray-500">{comment.time}</span>
                    </div>
                    <p className="text-sm text-gray-800">{comment.content}</p>
                  </div>
                  
                  {/* Comment Actions */}
                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                    <button 
                      onClick={() => likeComment(postId, comment.id)}
                      className="hover:text-linkedin-blue flex items-center space-x-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span>Like ({comment.likes})</span>
                    </button>
                    <button 
                      onClick={() => toggleReplyForm(postId, comment.id)}
                      className="hover:text-linkedin-blue"
                    >
                      Reply
                    </button>
                  </div>

                  {/* Reply Form */}
                  {showReplyForm[`${postId}-${comment.id}`] && (
                    <div className="flex space-x-2 mt-3">
                      <img 
                        src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face" 
                        alt="Your avatar" 
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <div className="flex-1 flex space-x-2">
                        <input
                          type="text"
                          placeholder="Write a reply..."
                          value={replyComments[`${postId}-${comment.id}`] || ''}
                          onChange={(e) => setReplyComments(prev => ({
                            ...prev,
                            [`${postId}-${comment.id}`]: e.target.value
                          }))}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleReply(postId, comment.id);
                            }
                          }}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded-full text-sm focus:outline-none focus:border-linkedin-blue"
                        />
                        <button
                          onClick={() => handleReply(postId, comment.id)}
                          disabled={!replyComments[`${postId}-${comment.id}`] || replyComments[`${postId}-${comment.id}`].trim() === ''}
                          className="text-linkedin-blue text-sm font-medium hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Reply
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Replies */}
                  {comment.replies && comment.replies.length > 0 && (
                    <div className="ml-4 mt-3 space-y-2">
                      {comment.replies.map((reply) => (
                        <div key={reply.id} className="flex space-x-2">
                          <img 
                            src={reply.avatar} 
                            alt={reply.author} 
                            className="w-6 h-6 rounded-full object-cover"
                          />
                          <div className="flex-1">
                            <div className="bg-gray-50 rounded-lg px-3 py-2">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-semibold text-xs text-gray-900">{reply.author}</span>
                                <span className="text-xs text-gray-500">{reply.time}</span>
                              </div>
                              <p className="text-xs text-gray-800">{reply.content}</p>
                            </div>
                            <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                              <button 
                                onClick={() => likeComment(postId, reply.id, true, comment.id)}
                                className="hover:text-linkedin-blue flex items-center space-x-1"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                <span>Like ({reply.likes})</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };


  useEffect(() => {
    getFeeds();
    setTitleDescription("Fractofolio - Feed", "The future of investment networking! Discover market insights, and make informed investment decisions with our community.")
  }, []);

  

  async function getFeeds() {
    setLoading(true);
    try {
      const apiURL = process.env.REACT_APP_API_URL;
      const response = await axios.post(`${apiURL}/feeds/get-companies`, {page: pageNumberRef.current});
      if(response.data.status == 'ok') {
        setFeedCompanies(prev => {
          // Создаем мапу уже существующих id для быстрой проверки
          const existingIds = new Set(prev.map(item => item.id));

          // Фильтруем новые элементы, исключая те, id которых уже есть
          const filteredNewItems = response.data.result.filter(item => !existingIds.has(item.id));

          // Возвращаем объединенный массив без дубликатов
          return [...prev, ...filteredNewItems];
        });

        setPageNumber(pageNumber+1);
        pageNumberRef.current = pageNumberRef.current + 1
      }

    } catch (error) {
      console.error("Error sending POST request:", error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="linkedin-layout bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="hidden lg:block lg:col-span-1">
            <LinkedInSidebar currentPage={currentPage} setCurrentPage={setCurrentPage} />
          </div>
          
          <div className="lg:col-span-3 space-y-4">
            {/* Hero Post */}
            <div className="linkedin-card">
              <div className="linkedin-card-header">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">FR</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Fractofolio</h3>
                    <p className="text-sm text-gray-600">Professional Investment Network • 1d</p>
                  </div>
                </div>
              </div>
              
              <div className="linkedin-card-content">
                <p className="text-gray-900 mb-4">
                  🚀 Welcome to the future of investment networking! Discover market insights, and make informed investment decisions with our community.
                </p>
                <div className="relative h-48 rounded-lg overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1462396240927-52058a6a84ec" 
                    alt="Corporate Building" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                    <h2 className="text-white text-2xl font-bold text-center">
                      Professional Investment Network
                    </h2>
                  </div>
                </div>
              </div>
              
              <div className="linkedin-card-actions">
                {/* <button className="linkedin-action-btn">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  Like
                </button> */}
                {/* <CommentSection postId="hero" /> */}
                {/* <button className="linkedin-action-btn">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  Share
                </button> */}
              </div>
              {/* <CommentSectionExpanded postId="hero" /> */}
            </div>

            {/* Stock Analysis Posts */}
            {feedCompanies.map((stock) => {
              const isExpanded = expandedDescriptions[stock.id] || false;
              const shouldTruncate = stock.description.length > MAX_LENGTH;
              const displayedText = isExpanded
                ? stock.description
                : stock.description.slice(0, MAX_LENGTH) + (shouldTruncate ? '...' : '');
                
              return (
                <div key={stock.id} className="linkedin-card">
                  <div className="linkedin-card-header">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        {/* <span className="font-bold text-linkedin-blue text-lg">{stock.symbol}</span> */}
                        <img src={stock.image} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{stock.company_name}</h3>
                        <p className="text-sm text-gray-600">{stock.sector}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-900">${stock.stock_price}</p>
                        {/* <p className={`text-sm font-medium ${stock.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {stock.change >= 0 ? '+' : ''}{stock.changePercent}%
                        </p> */}
                      </div>
                    </div>
                  </div>

                  <div className="linkedin-card-content">
                    <p className="text-sm text-gray-600">
                      {displayedText}
                    </p>

                    {shouldTruncate && (
                      <button
                        onClick={() => toggleDescription(stock.id)}
                        className="text-blue-500 text-sm mt-1 focus:outline-none"
                      >
                        {isExpanded ? 'Hide text' : 'Show more'}
                      </button>
                    )}

                  </div>
                  
                  <div className="linkedin-card-content">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="linkedin-metric">
                        <span className="linkedin-metric-label">Market Cap</span>
                        <span className="linkedin-metric-value">{Intl.NumberFormat('fr-FR').format(stock.market_cap)} M</span>
                      </div>
                      <div className="linkedin-metric">
                        <span className="linkedin-metric-label">P/E Ratio</span>
                        <span className="linkedin-metric-value">{stock.calculation_results.pe.pe.toFixed(2)}</span>
                      </div>
                      <div className="linkedin-metric">
                        <span className="linkedin-metric-label">PCF Ratio</span>
                        <span className="linkedin-metric-value">{stock.calculation_results.pcf.pcf.toFixed(2)}</span>
                      </div>
                      <div className="linkedin-metric">
                        <span className="linkedin-metric-label">PS Ratio</span>
                        <span className="linkedin-metric-value">{stock.calculation_results.ps.ps.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Success Rate</span>
                        <span className="text-sm font-bold text-gray-900">{stock.success_rate}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            stock.success_rate >= 85 ? 'bg-green-500' : 
                            stock.success_rate >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${stock.success_rate}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="linkedin-card-actions">
                    {/* <button className="linkedin-action-btn">
                      <Heart className="w-4 h-4 mr-1" />
                      Like
                    </button> */}
                    {/* <CommentSection postId={stock.id} /> */}
                    <button 
                      onClick={() => openCompanyPage(stock)}
                      className="linkedin-action-btn"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </button>
                  </div>
                  {/* <CommentSectionExpanded postId={stock.id} /> */}
                </div>
              );
            })}

            <div className='mx-auto w-fit'>
              <button ref={loadMoreRef} onClick={() => {getFeeds()}} className="linkedin-action-btn" disabled={loading}>
                {loading === false && (
                  <span>Load more companies</span>
                )}
                {loading === true && (
                  <span>Loading...</span>
                )}
              </button>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default FeedPage;